import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Typography,
    Select,
    MenuItem,
    TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enLocale from 'date-fns/locale/en-US';
import esLocale from 'date-fns/locale/es';

import * as Yup from 'yup';
import { FormikProps, useFormik } from 'formik';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { strengthColor, strengthIndicator } from 'utils/password-strength';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { StringColorProps } from 'types';
import './index.css';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import CountryAutocomplete, { CountryOption } from 'ui-component/CountryAutocomplete';
import { useAPI } from 'hooks/useApi';
import moment from 'moment';
import { notifySuccess } from 'utils';
import useConfig from 'hooks/useConfig';

interface FormValues {
    first_name: string;
    last_name: string;
    birth_date: string;
    email: string;
    country: string;
    password: string;
    repeat_password: string;
    privacy_policy: boolean;
    identity_verification: boolean;
    submit: null;
}

const styles = {
    input: {
        bgcolor: 'primary.light',
        borderRadius: '8px',
        '& fieldset': { border: 'none' },
        '.MuiOutlinedInput-input': {
            bgcolor: 'primary.light',
            height: '32px',
            color: '#132239'
        },
        '.MuiButtonBase-root': {
            color: 'rgba(0, 0, 0, 0.54)'
        }
    },
    checkbox: {
        '.MuiSvgIcon-root': { width: '18px', height: '18px' },
        color: '#132239',
        '&.MuiCheckbox-root.Mui-checked': {
            color: 'primary.800'
        }
    },
    select: {
        bgcolor: 'primary.light',
        borderRadius: '8px',
        '& fieldset': { border: 'none' },
        '.MuiOutlinedInput-input': {
            bgcolor: 'primary.light',
            height: '32px !important',
            display: 'flex',
            alignItems: 'center',
            color: '#132239'
        },
        '&.Mui-selected': {
            color: '#132239'
        }
    },
    submitButton: {
        fontSize: '16px',
        fontFamily: 'Rotonto',
        fontWeight: 500,
        backgroundColor: 'primary.800',
        borderRadius: '8px'
    },
    menuItem: {
        '&.Mui-selected': {
            color: 'primary.800'
        }
    }
};

type FormFieldType = {
    name: keyof Omit<FormValues, 'submit' | 'privacy_policy' | 'identity_verification'>;
    label: string;
    type: string;
    placeholder?: string;
    options?: Array<{ value: string; label: string }>;
    countryAsAlpha2?: boolean;
    gridProps: { xs: number; sm?: number };
};
const getFormFields = (t: TFunction): Array<FormFieldType> => {
    const formFields: Array<FormFieldType> = [
        {
            name: 'first_name',
            label: t('form_fields.labels.first_name') + '*',
            type: 'text',
            placeholder: t('form_fields.placeholders.first_name'),
            gridProps: { xs: 12, sm: 6 }
        },
        {
            name: 'last_name',
            label: t('form_fields.labels.last_name') + '*',
            type: 'text',
            placeholder: t('form_fields.placeholders.last_name'),
            gridProps: { xs: 12, sm: 6 }
        },
        {
            name: 'birth_date',
            label: t('form_fields.labels.date_of_birth') + '*',
            type: 'date',
            placeholder: t('form_fields.placeholders.date_of_birth'),
            gridProps: { xs: 12, sm: 6 }
        },
        {
            name: 'email',
            label: t('form_fields.labels.email') + '*',
            type: 'email',
            placeholder: t('form_fields.placeholders.email'),
            gridProps: { xs: 12, sm: 6 }
        },
        {
            name: 'country',
            label: t('form_fields.labels.country') + '*',
            type: 'country',
            placeholder: t('form_fields.placeholders.country'),
            gridProps: { xs: 12 }
        },
        {
            name: 'password',
            label: t('form_fields.labels.password') + '*',
            type: 'password',
            placeholder: t('form_fields.placeholders.password'),
            gridProps: { xs: 12, sm: 6 }
        },
        {
            name: 'repeat_password',
            label: t('form_fields.labels.repeat_password') + '*',
            type: 'password',
            placeholder: t('form_fields.placeholders.repeat_password'),
            gridProps: { xs: 12, sm: 6 }
        }
    ];
    return formFields;
};

type AuthRegisterProps = {
    onSuccess: Function;
};
const AuthRegister = ({ onSuccess }: AuthRegisterProps) => {
    const [passwordState, setPasswordState] = React.useState({
        show: false,
        strength: 0,
        level: undefined as StringColorProps | undefined
    });

    // const { firebaseRegister } = useAuth();
    const { t } = useTranslation();
    const { locale } = useConfig();
    const formFields = getFormFields(t);
    const api = useAPI('auth-register');
    const getShape = () => {
        return {
            first_name: Yup.string().max(255).required(requiredFieldMsg),
            last_name: Yup.string().max(255).required(requiredFieldMsg),
            birth_date: Yup.date().typeError(t('form_fields.validation_messages.field_is_invalid')).required(requiredFieldMsg),
            email: Yup.string().email(t('form_fields.validation_messages.field_is_invalid')).max(255).required(requiredFieldMsg),
            country: Yup.string().required(requiredFieldMsg),
            password: Yup.string().max(255).required(requiredFieldMsg),
            repeat_password: Yup.string()
                .oneOf([Yup.ref('password')], t('form_fields.validation_messages.passwords_must_match'))
                .required(requiredFieldMsg),
            privacy_policy: Yup.boolean().oneOf([true], t('form_fields.validation_messages.accept_privacy_policy')),
            identity_verification: Yup.boolean().oneOf([true], t('form_fields.validation_messages.accept_identity_verification_agreement'))
        };
    };

    const initialValues = {
        first_name: '',
        last_name: '',
        birth_date: '',
        email: '',
        country: '',
        password: '',
        repeat_password: '',
        privacy_policy: false,
        identity_verification: false,
        submit: null
    };
    const requiredFieldMsg = t('form_fields.validation_messages.field_is_required');
    const [validationSchema, setValidationSchema] = useState(Yup.object().shape(getShape()));
    const handleFormSubmit = async (values: typeof initialValues, { setErrors, setStatus, setSubmitting }: any) => {
        setSubmitting(true);
        const birthDate = moment(values.birth_date).format('MM/DD/YYYY');
        const toPost = { ...values, birth_date: birthDate };
        api.post('/leads/onboard', toPost)
            .then(({ data }) => {
                if (data.status === 'ok') {
                    notifySuccess(t('auth.register.sign_up_success'));
                    onSuccess();
                } else {
                    setStatus({ success: false });
                    setErrors({ submit: data.errors.join(' ') });
                }
            })
            .finally(() => {
                setSubmitting(false);
            });
    };
    const formik = useFormik({
        validationSchema: validationSchema,
        initialValues: initialValues,
        onSubmit: handleFormSubmit
    });

    const handlePasswordVisibility = () => {
        setPasswordState((prev) => ({ ...prev, show: !prev.show }));
    };

    const updatePasswordStrength = (value: string) => {
        const temp = strengthIndicator(value);
        setPasswordState((prev) => ({
            ...prev,
            strength: temp,
            level: strengthColor(temp)
        }));
    };

    useEffect(() => {
        updatePasswordStrength('123456');
    }, []);
    useEffect(() => {
        setValidationSchema(Yup.object().shape(getShape()));
        setTimeout(() => {
            formik.validateForm();
        }, 300);
    }, [locale]);

    const renderField = (field: (typeof formFields)[number], formikProps: FormikProps<FormValues>) => {
        switch (field.type) {
            case 'date':
                const maxDate = moment().format('YYYY-MM-DD');
                const adapterLocale = locale == 'en' ? enLocale : esLocale;
                return (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                        <DatePicker
                            value={formikProps.values[field.name]}
                            onChange={(newValue) => {
                                formikProps.setFieldValue(field.name, newValue);
                            }}
                            maxDate={maxDate}
                            renderInput={(props) => {
                                const tfProps = { ...props };
                                if (tfProps.inputProps) {
                                    tfProps.inputProps.placeholder = field.placeholder;
                                }
                                return (
                                    <TextField
                                        {...tfProps}
                                        fullWidth
                                        error={Boolean(formikProps.touched[field.name] && formikProps.errors[field.name])}
                                        sx={styles.input}
                                    />
                                );
                            }}
                        />
                    </LocalizationProvider>
                );
            case 'select':
                return (
                    <Select
                        value={formikProps.values[field.name]}
                        name={field.name}
                        onBlur={formikProps.handleBlur}
                        onChange={formikProps.handleChange}
                        displayEmpty
                        sx={styles.select}
                    >
                        <MenuItem value="" disabled sx={{ color: '#333' }}>
                            <Trans>{field.placeholder}</Trans>
                        </MenuItem>
                        {field.options?.map((option: any) => (
                            <MenuItem key={option.value} value={option.value} sx={styles.menuItem}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                );
            case 'country':
                return (
                    <CountryAutocomplete
                        onChange={(newValue: CountryOption) => {
                            if (field.countryAsAlpha2) {
                                formikProps.setFieldValue('country', newValue.country.alpha_2);
                            } else {
                                formikProps.setFieldValue('country', newValue.id);
                            }
                        }}
                        placeholder={field.placeholder}
                        valueAsAlpha2={field.countryAsAlpha2}
                        value={formikProps.values[field.name]}
                    />
                );
            case 'password':
                return (
                    <OutlinedInput
                        type={passwordState.show ? 'text' : 'password'}
                        value={formikProps.values[field.name]}
                        name={field.name}
                        onBlur={formikProps.handleBlur}
                        onChange={(e) => {
                            formikProps.handleChange(e);
                            if (field.name === 'password') {
                                updatePasswordStrength(e.target.value);
                            }
                        }}
                        placeholder={field.placeholder}
                        sx={styles.input}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={handlePasswordVisibility} edge="end" size="large">
                                    {passwordState.show ? (
                                        <Visibility
                                            sx={{
                                                color: 'rgba(0, 0, 0, 0.54)'
                                            }}
                                        />
                                    ) : (
                                        <VisibilityOff
                                            sx={{
                                                color: 'rgba(0, 0, 0, 0.54)'
                                            }}
                                        />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                );
            default:
                return (
                    <OutlinedInput
                        type={field.type}
                        value={formikProps.values[field.name]}
                        name={field.name}
                        onBlur={formikProps.handleBlur}
                        onChange={formikProps.handleChange}
                        placeholder={field.placeholder}
                        sx={styles.input}
                    />
                );
        }
    };

    return (
        <form noValidate onSubmit={formik.handleSubmit}>
            <Grid container spacing={2} mb={2}>
                {formFields.map((field) => (
                    <Grid item key={field.name} {...field.gridProps}>
                        <Typography variant="body2" color="#132239" sx={{ mb: 1 }}>
                            {field.label}
                        </Typography>
                        <FormControl
                            fullWidth
                            error={Boolean(formik.touched[field.name] && formik.errors[field.name])}
                            sx={{ borderRadius: '8px' }}
                        >
                            {renderField(field, formik)}
                            {formik.touched[field.name] && formik.errors[field.name] && (
                                <FormHelperText error>{formik.errors[field.name]}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                ))}
            </Grid>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={formik.values.privacy_policy}
                        onChange={formik.handleChange}
                        name="privacy_policy"
                        color="primary"
                        sx={styles.checkbox}
                    />
                }
                label={
                    <Typography variant="body2" color="#132239">
                        <Trans>auth.register.privacy_policy_agreement</Trans>
                    </Typography>
                }
            />
            {formik.touched.privacy_policy && formik.errors.privacy_policy && (
                <FormHelperText error>{formik.errors.privacy_policy}</FormHelperText>
            )}
            <FormControlLabel
                control={
                    <Checkbox
                        checked={formik.values.identity_verification}
                        onChange={formik.handleChange}
                        name="identity_verification"
                        color="primary"
                        sx={styles.checkbox}
                    />
                }
                label={
                    <Typography variant="body2" color="#132239">
                        <Trans>auth.register.identity_verification_agreement</Trans>
                    </Typography>
                }
            />
            {formik.touched.identity_verification && formik.errors.identity_verification && (
                <FormHelperText error>{formik.errors.identity_verification}</FormHelperText>
            )}
            {formik.errors.submit && (
                <Box sx={{ mt: 1 }}>
                    <FormHelperText error>{formik.errors.submit}</FormHelperText>
                </Box>
            )}
            <Box sx={{ mt: 2 }}>
                <AnimateButton>
                    <Button
                        disableElevation
                        disabled={formik.isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        color="secondary"
                        sx={styles.submitButton}
                    >
                        <Trans>auth.register.sign_up</Trans>
                    </Button>
                </AnimateButton>
            </Box>
        </form>
    );
};

export default AuthRegister;
